import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as PROMPTS from '../../data/prompt.constant';
import * as SAMPLE_DATA from '../../data/sample.constant';
import { EstimateService } from '../../estimate.service';
import { Observable, Subscription } from 'rxjs';
import { Prompt } from '../classes/prompt-class';
import { store, Phases, Level } from '../../interfaces/input-interfaces';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterModule } from '@angular/router';


@Component({
  standalone: true,
  selector: 'app-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss'],
  imports: [
    CommonModule,
    MatSlideToggleModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule
  ],
  providers: [{ provide: Prompt, deps: [EstimateService] }]
})
export class EstimateComponent implements OnInit, OnChanges, OnDestroy {

  // Input, Output
  @Input() subscription_id: string = '';
  // @Input() response: string = "";
  response: string = "";
  // @Output() displayResponse = new EventEmitter<any>();
  displayResponse = new EventEmitter<any>();

  // Public variables
  public formJson;
  public form: FormGroup;
  public showForm: boolean = false;
  public projectPhases: Phases;
  public placeholder: string;

  // Private variables
  private storeSubscription: Subscription;
  private formSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private progress: EstimateService,
    private http: HttpClient
  ) { }

  onSubmit() {
    if (this.storeSubscription) this.storeSubscription.unsubscribe();
    this.response = "";
    this.progress.resetProgress();
    this.progress.setSubscriptionId(this.subscription_id);
    this.openSubscription();
    this.stepZero();
    if (this.form.value.estimateAutoPilot) {
      this.progress.autoPilot.next(true)
    } else {
      this.progress.autoPilot.next(false)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.response && this.response !== "") {
      const store = this.progress.store.getValue();
      if (store.stepZero.status !== 'init' &&
        store.stepOne.status !== 'done') {
        this.stepOne(this.response);
      }
    }
  }

  handleSteps(store: any, inputFlag: string, outputFlag: string, doneFlag: string): void {
    if (store.stepZero.status === outputFlag && store.stepOne.status === outputFlag) {
      this.stepTwo(store);
    } else if (store.stepTwo.status === outputFlag) {
      this.stepThree(store);
    } else if (store.stepThree.status === inputFlag) {
      this.stepFour(store);
    } else if (store.stepThree.status === doneFlag && store.stepFour.status !== doneFlag && store.stepFour.status !== outputFlag) {
      this.gatherResults(store);
    }
  }

  stepZero(): void {
    // Call one uses eventEmitter
    this.progress.createPrompt(
      'stepZero',
      PROMPTS.stepZeroInstruction,
      {
        "INPUT-TEXT": this.form.value.estimateInputText
      },
      'eventEmitModel',
      false,
      '1',
      this.displayResponse
    );
  }

  stepOne(response: string): void {
    // Next line needed due to api call via eventEmitter
    this.progress.prompts[0].setProcessed(response);
    this.progress.createPrompt(
      'stepOne',
      PROMPTS.stepOneInstruction,
      { "INPUT-TEXT": this.form.value.estimateInputText }
    );
  }

  stepTwo(store: any): void {
    if (this.progress.timeSensitiveDetails.getValue().length !== 0) {
      this.setPromptsDone([0, 1]);
      this.progress.createPrompt('stepTwo', PROMPTS.stepTwoInstruction, {
        "PROJECT-TYPE": store.stepOne.feedback[0],
        "INPUT-TEXT": this.progress.timeSensitiveDetails.getValue().toString()
      });
    } else {
      setTimeout(() => {
        this.stepTwo(store)
      }, 500);
    }
  }

  stepThree(store: any): void {
    this.setPromptsDone([2]);
    const timeFrame = String(Number((store.stepTwo.feedback[0].match(/\d+/)[0])) - 1) + ' ' + this.progress.extractTimeUnit(store.stepTwo.feedback[0]);
    this.progress.createPrompt('stepThree', PROMPTS.stepThreeInstruction, {
      "PROJECT-TYPE": store.stepOne.feedback[0],
      "INPUT-TEXT": store.stepOne.feedback[2] + ' ' + this.progress.summary.getValue()
        .replace(new RegExp(`<BULLET>`, 'g'), '')
        .replace(new RegExp(`</BULLET>`, 'g'), ', ')
        .replace('OUTPUT EXAMPLE:', ''),
      "TIME-FRAME": timeFrame,
    }, 'text-bison', true, 'semi-strict', undefined, true, true);
  }

  stepFour(store: any): void {
    this.setPromptsDone([3]);
    this.projectPhases = JSON.parse(store.stepThree.response[0]);
    this.projectPhases['phases']?.forEach((phase, index) => {
      setTimeout(() => {
        this.createPhasePrompt(phase, index, store);
      }, 200 * index);
    });
  }

  createPhasePrompt(phase: any, index: number, store: any): void {
    const timeFrame = phase['phaseTime'].match(/\d+/)[0] + ' ' + this.progress.extractTimeUnit(phase['phaseTime']);
    this.progress.createPrompt('stepFour', PROMPTS.stepFourInstruction, {
      "PROJECT-TYPE": store.stepOne.feedback[0],
      "PROJECT-HEADER": this.progress.projectHeadline.getValue(),
      "INPUT-TEXT": phase['phaseDescription'],
      "TIME-FRAME": timeFrame,
      "PROJECT-PHASE": phase['phaseTitle'],
      "PHASE-ID": index + 1,
      "CONTEXT": this.progress.summary.getValue()
        .replace(new RegExp(`<BULLET>`, 'g'), '')
        .replace(new RegExp(`</BULLET>`, 'g'), ', ')
        .replace('OUTPUT EXAMPLE:', ''),
    }, 'text-bison', true, 'semi-strict');
  }

  gatherResults(store: store): void {
    // Wait for all responses of all phases
    if (this.projectPhases && this.projectPhases['phases']?.length === store.stepFour.response?.length) {
      this.progress.prompts[4]?.setDone();
    }
  }

  // Helper functions
  openSubscription(): void {
    this.storeSubscription = this.progress.store.subscribe({
      next: (store) => this.handleSteps(store, 'afterInput', 'afterOutput', 'done'),
      error: (e) => console.error(e),
    });
  }

  setPromptsDone(indices: number[]): void {
    indices.forEach(index => this.progress.prompts[index].setDone());
  }

  // Form elements
  ngOnInit(): void {
    this.getFormJson();
  }

  prefillSampleData() {
    this.form.patchValue(SAMPLE_DATA.form_value);
  }

  getFormJson() {
    const formJson = {
      "data": [
        {
          "name": "estimateInputText",
          "type": "textarea",
          "label": "Project Description"
        },
        {
          "name": "estimateAutoPilot",
          "type": "toggle",
          "label": "Do you want to run in Auto Pilot mode?"
        }
      ]
    }
    this.formJson = formJson.data;
    this.initForm();
  }

  initForm() {
    this.placeholder = SAMPLE_DATA.form_value.estimateInputText;
    this.form = this.formBuilder.group(
      this.formJson.reduce(
        (acc, control) => ({ ...acc, [control.name]: '' }),
        {}
      )
    );
    this.showForm = true;
  }

  onKeydown(event: KeyboardEvent, name: string): void {
    if (event.key === 'Tab') {
      event.preventDefault();
      const control = this.form.get(name);
      control.setValue(this.placeholder)
    }
  }

  ngOnDestroy(): void {
    this.storeSubscription.unsubscribe();
    this.formSubscription.unsubscribe();
  }
}
